import { useMachine } from "@xstate/react";
import * as React from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useForm } from "react-yup";
import * as Yup from "yup";

import {
  AsyncTypeaheadSearch,
  FilterName,
  FriendlyKeyValueUrl,
  OpportunitySearchResults,
  useSearchFilters,
} from "@skv/core";

import {
  opportunityResultStore,
  OpportunitySearchQueryState,
  SearchFormEventBase,
  useGlobal,
  useOpportunitySearchQuery,
  useThunkDispatch,
} from "@skv/state";

import { SelectMulti } from "./Form";

import { LocationSearchTypeahead } from "./LocationSearchTypeahead";
import { searchFormMachine } from "./SearchForm.state";

const SearchSchema = Yup.object({
  keyword: Yup.string().required().default("").label("Keyword"),
  where: Yup.string().required().default("").label("Where"),
  commitment: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Commitment"),
  category: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Category"),
  daytime: Yup.string().label("Day and time"),
  suitablefor: Yup.string().label("Suitable for"),
  typeofwork: Yup.string().label("Type of work"),
});

export const SearchFormWithCommitment = (): JSX.Element => {
  const { push, replace } = useHistory();

  const formRef = React.useRef<HTMLFormElement | null>(null);
  const location = useLocation<{
    analytics?: {
      didKeywordAutoComplete: boolean;
      didWhereAutoComplete: boolean;
    };
    resetSearch?: boolean;
    searchQuery?: ReturnType<typeof useOpportunitySearchQuery>;
    shouldUpdateSearchForm?: boolean;
  }>();

  const dispatch = useThunkDispatch();
  const searchQuery = useOpportunitySearchQuery();

  const { categoryList } = useGlobal();

  const match = useRouteMatch({
    path: ["/*-volunteering", "/volunteering*"],
  });

  const initialValues = React.useMemo(
    () => ({
      keyword: "",
      where: "",
      commitment: [] as number[],
      category: [] as number[],
      suitablefor: "",
      typeofwork: "",
    }),
    []
  );

  const {
    filters,
    handleFiltersChange,
    handleSelectChange,
    handleSelectClear,
  } = useSearchFilters({
    searchQuery,
    onChange: (nextQuery) => {
      dispatch(
        opportunityResultStore.actions.fetchOpportunities(nextQuery)
      ).then((data) => {
        push(data.canonicalUrl, {
          searchQuery: nextQuery,
          analytics: location.state?.analytics,
        });
      });
    },
  });

  const [isRepeatSearch, setIsRepeatSearch] = React.useState<boolean>(() => {
    if (
      match?.isExact ||
      location.pathname.includes("/volunteering") ||
      location.search
    ) {
      return true;
    }
    return !!searchQuery.category.length;
  });
  const [openFilter, setOpenFilter] = React.useState<FilterName | null>(null);
  const [showFilters, setShowFilters] = React.useState<boolean>(isRepeatSearch);

  const form = useForm({
    defaultValues: initialValues,
    validationSchema: SearchSchema,
  });

  const { setValues } = form;
  const [current, send, actorRef] = useMachine(searchFormMachine);

  React.useEffect(() => {
    // If the user clicks on the Opportunities tab, or the SEEK logo,
    // pass through the resetSearch flag to reset the search form.
    if (location.state?.resetSearch) {
      setShowFilters(location.pathname === "/volunteering");
      setIsRepeatSearch(location.pathname === "/volunteering");
      setValues(() => initialValues);
      send({
        type: "CLEAR_ALL_INPUTS",
      });
      return;
    }

    // On location change populate the WHAT input
    if (searchQuery.keyword) {
      send({ type: "KEYWORD_INPUT_CHANGE", value: searchQuery.keyword });
    } else if (searchQuery.category.length === 1) {
      send({
        type: "KEYWORD_INPUT_SELECT",
        value: categoryList.find(({ id }) => id === searchQuery.category[0]),
      });
    }

    // On location change populate the WHERE input
    if (searchQuery.where) {
      if (location.state?.analytics?.didWhereAutoComplete) {
        send({ type: "LOCATION_INPUT_SELECT", value: searchQuery.where });
      } else {
        send({ type: "LOCATION_INPUT_CHANGE", value: searchQuery.where });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const categoryOptions = React.useMemo(() => {
    return (categoryList || []).filter(
      (n) =>
        n.name
          .toLocaleLowerCase()
          .indexOf(form.values.keyword.toLocaleLowerCase()) > -1
    );
  }, [categoryList, form.values.keyword]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowFilters(true);
    setIsRepeatSearch(true);

    const query: OpportunitySearchQueryState = {
      ...location.state?.searchQuery,
      keyword: current.context.didKeywordAutoComplete
        ? ""
        : current.context.keyword,
      category: isRepeatSearch
        ? location.state?.searchQuery.category || []
        : current.context.category,
      commitment: isRepeatSearch
        ? location.state?.searchQuery.commitment || []
        : form.values.commitment,
      where: current.context.where,
    };

    if ("page" in query) {
      delete query.page;
    }

    const createNextHistoryState = (data: OpportunitySearchResults) => {
      return {
        searchQuery: {
          ...query,
          where: data.canonicalLocation || query.where,
        },
        analytics: {
          didKeywordAutoComplete: current.context.didKeywordAutoComplete,
          didWhereAutoComplete: current.context.didWhereAutoComplete,
        },
      };
    };

    /*
      push to /volunteering url to show search page
      if current url is not on the search page.
      This is needed to show the loading states,
      otherwise on a slow connection for example,
      it would just stay on the home page and only change
      page when the request is finished, and the user will
      not see the loading indicator.
    */
    if (!match) {
      push(`/volunteering`, { searchQuery: query });
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => {
          replace(data.canonicalUrl, createNextHistoryState(data));
        }
      );
    } else {
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => {
          push(data.canonicalUrl, createNextHistoryState(data));
        }
      );
    }
  };

  const onOptionSelected = (
    selected: Array<FriendlyKeyValueUrl | string>,
    field: FilterName
  ) => {
    const action: SearchFormEventBase["type"] =
      field === "keyword" ? "KEYWORD_INPUT_SELECT" : "LOCATION_INPUT_SELECT";

    if (selected.length) {
      Promise.resolve()
        .then(() => {
          send({
            type: action,
            value: selected[0],
          } as SearchFormEventBase);
        })
        .then(() => {
          setOpenFilter(null);
          searchQuery[field as string] = "";
          if (isRepeatSearch) {
            if (field === "keyword" && typeof selected[0] !== "string") {
              handleSelectChange("category", selected[0].id);
            }
            if (field === "where") {
              formRef.current?.requestSubmit();
            }
          }
        });
    }
  };

  const getWhatValueFromContext = () => {
    if (current.context.keyword) {
      return current.context.keyword;
    }

    if (current.context.category.length) {
      const selectedId = current.context.category[0];
      return categoryList.find((n) => n.id === selectedId)?.name || "";
    }

    return "";
  };

  React.useEffect(() => {
    if (
      !location.state ||
      typeof location.state.shouldUpdateSearchForm === "undefined"
    ) {
      return;
    }

    if (location.state.shouldUpdateSearchForm === true) {
      send({
        type: "KEYWORD_INPUT_CHANGE",
        value: location.state?.searchQuery?.keyword || "",
      });

      send({
        type: "LOCATION_INPUT_CHANGE",
        value: location.state?.searchQuery?.where || "",
      });
    }
  }, [location.state, send]);

  const commitmentList =
    filters
      .find((n) => n.name === "commitment")
      ?.data.map((n) => ({ id: `${n.id}`, name: n.name })) || [];

  return (
    <section>
      <div className="nsw-container mt-n5">
        <div className="bg-secondary p-4">
          <form.FormProvider>
            <form
              ref={formRef}
              className="nsw-form"
              css={`
                .form-control {
                  border-color: #fff;
                }
              `}
              method="GET"
              action="/volunteering"
              onSubmit={handleSubmit}
            >
              <div>
                <div className="row align-items-end mx-n2 mt-lg-n3">
                  {/* CATEGORY / KEYWORD */}
                  <div
                    className="col-md nsw-form__group d-flex flex-column px-2"
                    css={`
                      overflow-x: clip;
                    `}
                  >
                    <label className="nsw-form__label" htmlFor="keyword">
                      What
                    </label>
                    <span className="sr-only">What are you interested in?</span>
                    <AsyncTypeaheadSearch<typeof categoryOptions[0]>
                      id="keyword"
                      inputProps={{
                        name: "keyword",
                        autoComplete: "off",
                        placeholder: "What are you interested in?",
                        className:
                          "nsw-form__input focus-primary border-primary",
                      }}
                      labelKey={(option) => option.name}
                      onInputChange={(text) => {
                        const id = current.context.category[0];
                        if (
                          id &&
                          isRepeatSearch &&
                          location.state?.searchQuery.category.includes(id)
                        ) {
                          handleSelectChange("category", id);
                        }
                        send({ type: "KEYWORD_INPUT_CHANGE", value: text });
                      }}
                      onSearch={() => {
                        return;
                      }}
                      onChange={(selected) =>
                        onOptionSelected(selected, "keyword")
                      }
                      onClear={() => {
                        Promise.resolve().then(() => {
                          setOpenFilter(null);
                          send({ type: "KEYWORD_INPUT_CLEAR" });

                          const id = current.context.category[0];
                          if (
                            id &&
                            isRepeatSearch &&
                            location.state?.searchQuery.category.includes(id)
                          ) {
                            handleSelectChange("category", id);
                          } else {
                            searchQuery["keyword"] = "";
                            if (isRepeatSearch) {
                              formRef.current?.requestSubmit();
                            }
                          }
                        });
                      }}
                      open={
                        openFilter === "keyword" && categoryOptions.length > 0
                      }
                      onClick={() => {
                        if (openFilter !== "keyword") {
                          setOpenFilter("keyword");
                        }
                      }}
                      onFocus={() => setOpenFilter("keyword")}
                      onBlur={() => setOpenFilter(null)}
                      onKeyDown={(e) => {
                        switch (e.key) {
                          case "ArrowUp":
                          case "ArrowDown":
                            if (openFilter !== "keyword") {
                              setOpenFilter("keyword");
                              return false;
                            }
                            break;
                          case "Escape":
                            if (openFilter === "keyword") {
                              setOpenFilter(null);
                            }
                            break;
                          case "Enter":
                            formRef.current?.requestSubmit();
                            break;
                          default:
                            break;
                        }
                      }}
                      isLoading={current.matches("relatedSuggestions.loading")}
                      options={categoryOptions}
                      defaultInputValue={getWhatValueFromContext()}
                      selected={
                        current.context.keyword
                          ? []
                          : current.context.category
                              .map((id) =>
                                categoryList.find((l) => l.id === id)
                              )
                              .filter((n) => n != null)
                      }
                      minLength={0}
                      renderMenu={(
                        results,
                        menuProps,
                        { Menu, MenuItem, MenuHeader }
                      ): JSX.Element => {
                        return (
                          <Menu
                            {...menuProps}
                            ref={
                              menuProps.ref as React.MutableRefObject<HTMLUListElement>
                            }
                            aria-labelledby="category-heading"
                          >
                            <MenuHeader
                              id="category-heading"
                              className="dropdown-header font-weight-bold"
                            >
                              Categories
                            </MenuHeader>
                            {results.map((n, idx) => (
                              <MenuItem key={idx} option={n} position={idx} />
                            ))}
                          </Menu>
                        );
                      }}
                    />
                  </div>

                  {/* COMMITMENT */}
                  <div className="col-md nsw-form__group px-2">
                    <SelectMulti
                      id="Commitment"
                      name="Commitment"
                      label="Commitment"
                      labelProps={{
                        className: "nsw-form__label",
                      }}
                      categoryTitle="Any commitment"
                      categoryType="commitments"
                      items={commitmentList}
                      defaultValue={
                        isRepeatSearch
                          ? searchQuery["commitment"].map(String)
                          : form.values.commitment.map(String)
                      }
                      onClear={() => {
                        if (isRepeatSearch) {
                          handleSelectClear("commitment");
                        } else {
                          form.setValue("commitment", []);
                        }
                      }}
                      onDropdownOpen={() => setOpenFilter(null)}
                      onUpdateField={(value) => {
                        const id = Number(value);
                        if (isRepeatSearch) {
                          handleSelectChange("commitment", id);
                        } else {
                          const updatedList = form.values.commitment.includes(
                            id
                          )
                            ? form.values.commitment.filter((n) => n !== id)
                            : [...form.values.commitment, id];

                          form.setValue("commitment", updatedList);
                        }
                      }}
                    />
                  </div>

                  {/* LOCATION */}
                  <div
                    className="col-md nsw-form__group d-flex flex-column px-2"
                    css={`
                      overflow-x: clip;
                    `}
                  >
                    <LocationSearchTypeahead
                      actorRef={actorRef}
                      openFilter={openFilter}
                      setOpenFilter={setOpenFilter}
                      isRepeatSearch={isRepeatSearch}
                      onOptionSelected={onOptionSelected}
                      formRef={formRef}
                    />
                  </div>

                  <div className="px-2 nsw-form__group flex-grow-1 flex-md-grow-0">
                    <button
                      type={isRepeatSearch ? "button" : "submit"}
                      aria-label="Find opportunities"
                      className="btn btn-block btn-primary"
                      css={`
                        min-width: 100px;
                      `}
                      onClick={() => {
                        if (isRepeatSearch) {
                          formRef.current?.requestSubmit();
                        }
                      }}
                    >
                      Find
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </form.FormProvider>
        </div>
      </div>
    </section>
  );
};
